import { useState, useEffect, useCallback } from 'react';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { toast } from '../../../../util/toasts';
import { Field } from '../../../../components/field/field';
import { HourTrackingConfig } from '../../../../clients/quipli-service/product/product';

export const HourTrackingConfigForm = ({ productId }: { productId?: number }) => {
  const [original, setOriginal] = useState<HourTrackingConfig>();
  const [dailyHoursLimit, setDailyHoursLimit] = useState<number>();
  const [hourlyOverageRate, setOverageRate] = useState<number>();
  const [isLoading, setIsLoading] = useState(true);
  const { getHourTrackingConfigs, upsertHourTrackingConfig, deleteHourTrackingConfig } = useAPI();

  const updateConfig = useCallback(async () => {
    if (!isLoading && productId) {
      try {
        if (
          dailyHoursLimit !== original?.dailyHoursLimit ||
          Math.round((hourlyOverageRate || 0) * 100) !== original?.hourlyOverageRate
        ) {
          if (dailyHoursLimit && dailyHoursLimit > 0) {
            await upsertHourTrackingConfig({
              productId,
              dailyHoursLimit,
              hourlyOverageRate: hourlyOverageRate ? Math.round(hourlyOverageRate * 100) : undefined,
            });
            toast('Hour tracking config updated', 'success');
          } else {
            await deleteHourTrackingConfig(productId);
          }
        }
      } catch (e) {
        toast('Error updating hour tracking config', 'error');
      }
    }
  }, [original, dailyHoursLimit, hourlyOverageRate]);

  useEffect(() => {
    const fetchHourTrackingConfig = async () => {
      if (!productId) return;
      setIsLoading(true);
      const config = await getHourTrackingConfigs([productId]);
      if (config.length > 0) {
        setOriginal(config[0]);
        setDailyHoursLimit(config[0].dailyHoursLimit);
        setOverageRate(config[0].hourlyOverageRate ? config[0].hourlyOverageRate / 100 : undefined);
      }
      setIsLoading(false);
    };
    fetchHourTrackingConfig();
  }, []);

  useEffect(() => {
    const timeOutId = setTimeout(() => {
      updateConfig();
    }, 500);
    return () => clearTimeout(timeOutId);
  }, [dailyHoursLimit, hourlyOverageRate]);

  return (
    <div className="hour-tracking-config">
      <div className="mt-2 mb-4 lg:flex lg:items-center">
        <div>
          <label className="mb-0.5">Daily Hours Limit</label>
          <Field
            className="md:min-w-[250px]"
            type="number"
            maximumFractionDigits={0}
            value={dailyHoursLimit ?? ''}
            onChange={(v) => setDailyHoursLimit(v)}
          />
        </div>
        <span className="italic mt-2.5 lg:ml-3">If left blank, overage fees will not apply.</span>
      </div>
      <div className="mb-2 lg:flex lg:items-center">
        <div>
          <label>Overage Rate</label>
          <Field
            className="md:min-w-[250px]"
            type="currency"
            maximumFractionDigits={2}
            value={hourlyOverageRate ?? ''}
            onChange={(v) => setOverageRate(v)}
          />
        </div>
        {/* TODO: also calculate overage charges based on below, but not while inventory return 1.0 because that'll take way to long to figure out and isn't it moving to 2.0 soon anyway... */}
        {/* <span className="italic mt-2.5 lg:ml-3">
          If left blank, the overage charge will be calculated based on the rate used by rental (e.g. if 8 hours allowed
          per day and $80 daily rental rate, 1 extra hour costs $10). Set to 0 if no overage fees should apply.
        </span> */}
      </div>
    </div>
  );
};
